import { api } from "./apiSlice";
import { showMessage } from "../../helpers";
import { useGetPartnerListQuery } from "./adminPartnerApi";

export const complianceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAssignedComplianceList: builder.query({
      query: (partnerId) => `/template-letter/admin/${partnerId}/`,
      providesTags: (result, error, partnerId) => [
        { type: "AssignedCompliance", id: partnerId },
      ],
    }),

    getAdminComplianceList: builder.query({
      query: () => "/template-letter-create/",
      providesTags: ["AdminComplianceList"],
    }),

    assignTemplate: builder.mutation({
      query: (data) => ({
        url: "/template-letter/admin/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { partner_id }) => [
        { type: "AssignedCompliance", id: partner_id },
        "AssignedCompliance",
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Compliance assigned successfully!");
        } catch (error) {
          if (error?.error?.data?.message) {
            showMessage("error", error.error.data.message.toString());
          } else {
            showMessage("error", "Something Went Wrong!!");
          }
        }
      },
    }),

    // Partner Compliance Endpoints
    getPartnerComplianceList: builder.query({
      query: (params) => ({
        url: "/template-letter/admin/",
        params,
      }),
      providesTags: ["PartnerCompliance"],
    }),

    getPartnerComplianceLetterList: builder.query({
      query: (params) => ({
        url: "/compliance-letter/partner/",
        params,
      }),
      providesTags: ["PartnerComplianceLetterList"],
    }),

    sendComplianceReminder: builder.mutation({
      query: (data) => ({
        url: "/compliance-letter-reminder/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Reminder sent successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    sendComplianceMail: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/html_template/${id}/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Mail sent successfully!");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    downloadTemplate: builder.mutation({
      query: (data) => ({
        url: "/download-compliance-letter/",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data?.url) window.open(response.data.url);
        } catch (error) {
          console.log(error);
        }
      },
    }),

    downloadSignedTemplate: builder.mutation({
      query: (data) => ({
        url: "/download-compliance-letter/",
        method: "POST",
        body: data,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          if (response.data?.url) window.open(response.data.url);
        } catch (error) {
          console.log(error);
        }
      },
    }),

    verifySignedLetter: builder.mutation({
      query: (data) => ({
        url: "/compliance-template-letter/approve/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Verified");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    rejectSignedLetter: builder.mutation({
      query: (data) => ({
        url: "/compliance-template-letter/approve/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Rejected");
        } catch (error) {
          showMessage("error", error.toString());
        }
      },
    }),

    deletePartnerTemplate: builder.mutation({
      query: ({ partner_id, ...data }) => ({
        url: `/template-letter/admin/${partner_id}/`,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Template Deleted!");
        } catch (error) {
          console.log(error);
        }
      },
    }),

    sendBulkReminder: builder.mutation({
      query: (data) => ({
        url: "/compliance-bulk-reminder/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerCompliance"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Bulk Reminder Sent!");
        } catch (error) {
          console.log(error);
        }
      },
    }),

    getNotifications: builder.query({
      query: () => "/admin/notification/",
      providesTags: ["Notifications"],
    }),

    markNotificationsRead: builder.mutation({
      query: () => ({
        url: "/admin/notification-read/",
        method: "POST",
      }),
      invalidatesTags: ["Notifications"],
    }),
  }),
});

export const {
  useGetAssignedComplianceListQuery,
  useGetAdminComplianceListQuery,
  useAssignTemplateMutation,
  useGetPartnerComplianceListQuery,
  useGetPartnerComplianceLetterListQuery,
  useSendComplianceReminderMutation,
  useSendComplianceMailMutation,
  useDownloadTemplateMutation,
  useDownloadSignedTemplateMutation,
  useVerifySignedLetterMutation,
  useRejectSignedLetterMutation,
  useDeletePartnerTemplateMutation,
  useSendBulkReminderMutation,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} = complianceApi;

// Re-export from adminPartnerApi for convenience
export { useGetPartnerListQuery as useGetPartnersListQuery };
